import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  IconButton,
} from '@mui/material';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import {
  useLazySearchGooglePlacesQuery,
  useImportSelectedPlacesMutation,
} from '../../redux/adminApiSlice';

const STORAGE_KEY = 'quickPlaceImport_selectedTypes';
const STORAGE_KEY_LIMIT = 'quickPlaceImport_maxPlaces';
const DEFAULT_RADIUS_METERS = 500;
const DEFAULT_MAX_PLACES = 25;
const NYC_FALLBACK = { lat: 40.758, lng: -73.9855 };
const MAX_PLACES_OPTIONS = [10, 25, 50, 100];

const OSM_PLACE_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'fast_food', label: 'Fast Food' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'bar', label: 'Bar' },
  { value: 'pub', label: 'Pub' },
  { value: 'nightclub', label: 'Nightclub' },
  { value: 'cinema', label: 'Cinema' },
  { value: 'theatre', label: 'Theatre' },
  { value: 'museum', label: 'Museum' },
  { value: 'art_gallery', label: 'Art Gallery' },
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'convenience', label: 'Convenience' },
  { value: 'clothes', label: 'Clothing' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'gym', label: 'Gym' },
  { value: 'park', label: 'Park' },
  { value: 'library', label: 'Library' },
  { value: 'school', label: 'School' },
  { value: 'university', label: 'University' },
];

interface OSMPlace {
  id: string;
  name: string;
  type: string;
  coordinates: { lat: number; lng: number };
  isDuplicate?: boolean;
}

function loadSelectedTypes(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return ['restaurant', 'bar', 'cafe', 'pub'];
}

function saveSelectedTypes(types: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(types));
}

/** Map OSM place types to Google Places API types */
function mapOSMToGoogleTypes(osmTypes: string[]): string[] {
  const mapping: Record<string, string[]> = {
    restaurant: ['restaurant'],
    fast_food: ['fast_food_restaurant'],
    cafe: ['cafe', 'coffee_shop'],
    bar: ['bar'],
    pub: ['bar'],
    nightclub: ['night_club'],
    cinema: ['movie_theater'],
    theatre: ['movie_theater'],
    museum: ['museum'],
    art_gallery: ['art_gallery'],
    supermarket: ['supermarket'],
    convenience: ['convenience_store'],
    clothes: ['clothing_store'],
    bakery: ['bakery'],
    gym: ['gym'],
    park: ['park'],
    library: ['library'],
    school: ['school'],
    university: ['university'],
  };
  const googleTypes = new Set<string>();
  for (const osm of osmTypes) {
    (mapping[osm] || [osm]).forEach((g) => googleTypes.add(g));
  }
  return Array.from(googleTypes);
}

function loadMaxPlaces(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LIMIT);
    if (stored) {
      const n = parseInt(stored, 10);
      if (!isNaN(n) && MAX_PLACES_OPTIONS.includes(n)) return n;
    }
  } catch {
    // ignore
  }
  return DEFAULT_MAX_PLACES;
}

const QuickPlaceImport: React.FC = () => {
  const [placeName, setPlaceName] = useState('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [addPlaceResult, setAddPlaceResult] = useState<{ success: boolean; message: string } | null>(null);
  const [aroundMePlaces, setAroundMePlaces] = useState<OSMPlace[]>([]);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<string>>(new Set());
  const [aroundMeResult, setAroundMeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [placeTypesModalOpen, setPlaceTypesModalOpen] = useState(false);
  const [selectedPlaceTypes, setSelectedPlaceTypes] = useState<string[]>(loadSelectedTypes);
  const [maxPlaces, setMaxPlaces] = useState(loadMaxPlaces);

  const [searchGooglePlaces, { isLoading: isSearching }] = useLazySearchGooglePlacesQuery();
  const [importSelected, { isLoading: isImporting }] = useImportSelectedPlacesMutation();

  useEffect(() => {
    saveSelectedTypes(selectedPlaceTypes);
  }, [selectedPlaceTypes]);

  const getLocation = useCallback((): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }, []);

  const handleAddPlace = async () => {
    const name = placeName.trim();
    if (!name) {
      setAddPlaceResult({ success: false, message: 'Enter a place name' });
      return;
    }
    setAddPlaceResult(null);
    setLocationError(null);
    try {
      const coords = await getLocation();
      const result = await importSelected({
        places: [{ name, latitude: coords.lat, longitude: coords.lng }],
      }).unwrap();
      const imported = result.stats?.totalPlacesImported ?? 0;
      setAddPlaceResult({
        success: imported > 0,
        message: imported > 0
          ? `Imported "${name}"`
          : result.errors?.[0] || result.results?.[0]?.reason || 'Import failed',
      });
      if (imported > 0) setPlaceName('');
    } catch (err: any) {
      if (err?.message?.includes('denied') || err?.code === 1) {
        setLocationError('Location denied. Using NYC as fallback.');
        const result = await importSelected({
          places: [{ name, latitude: NYC_FALLBACK.lat, longitude: NYC_FALLBACK.lng }],
        }).unwrap();
        const imported = result.stats?.totalPlacesImported ?? 0;
        setAddPlaceResult({
          success: imported > 0,
          message: imported > 0 ? `Imported "${name}" (NYC)` : result.errors?.[0] || 'Import failed',
        });
        if (imported > 0) setPlaceName('');
      } else {
        setAddPlaceResult({
          success: false,
          message: err?.data?.message || err?.message || 'Import failed',
        });
      }
    }
  };

  const handleAroundMe = async () => {
    setLocationError(null);
    setAroundMePlaces([]);
    setSelectedPlaceIds(new Set());
    setAroundMeResult(null);
    try {
      const coords = await getLocation();
      const includedTypes = selectedPlaceTypes.length > 0
        ? mapOSMToGoogleTypes(selectedPlaceTypes)
        : undefined;
      const result = await searchGooglePlaces({
        latitude: coords.lat,
        longitude: coords.lng,
        radiusMeters: DEFAULT_RADIUS_METERS,
        includedTypes,
      }).unwrap();
      setAroundMePlaces(result.places || []);
      if (!result.places?.length) {
        setAroundMeResult({ success: false, message: 'No places found nearby' });
      }
    } catch (err: any) {
      if (err?.message?.includes('denied') || err?.code === 1) {
        setLocationError('Location denied. Using NYC.');
        const includedTypes = selectedPlaceTypes.length > 0
          ? mapOSMToGoogleTypes(selectedPlaceTypes)
          : undefined;
        const result = await searchGooglePlaces({
          latitude: NYC_FALLBACK.lat,
          longitude: NYC_FALLBACK.lng,
          radiusMeters: DEFAULT_RADIUS_METERS,
          includedTypes,
        }).unwrap();
        setAroundMePlaces(result.places || []);
      } else {
        setAroundMeResult({
          success: false,
          message: err?.data?.message || err?.message || 'Search failed',
        });
      }
    }
  };

  const handleImportAroundMe = async () => {
    const toImport = aroundMePlaces.filter(
      (p) => selectedPlaceIds.has(p.id) && !p.isDuplicate
    );
    if (toImport.length === 0) {
      setAroundMeResult({ success: false, message: 'Select at least one place' });
      return;
    }
    setAroundMeResult(null);
    try {
      const result = await importSelected({
        places: toImport.map((p) => ({
          name: p.name,
          latitude: p.coordinates.lat,
          longitude: p.coordinates.lng,
        })),
      }).unwrap();
      const imported = result.stats?.totalPlacesImported ?? 0;
      setAroundMeResult({
        success: imported > 0,
        message: `Imported ${imported} place(s)`,
      });
      setSelectedPlaceIds(new Set());
    } catch (err: any) {
      setAroundMeResult({
        success: false,
        message: err?.data?.message || err?.message || 'Import failed',
      });
    }
  };

  const togglePlaceType = (value: string) => {
    setSelectedPlaceTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const togglePlaceSelection = (id: string) => {
    const place = aroundMePlaces.find((p) => p.id === id);
    if (place?.isDuplicate) return;
    setSelectedPlaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const touchTargetSx = { minHeight: 48, py: 1.5 };

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 480,
        mx: 'auto',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        Quick Place Import
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add places by name or discover places around you.
      </Typography>

      {locationError && (
        <Alert severity="info" onClose={() => setLocationError(null)}>
          {locationError}
        </Alert>
      )}

      {/* Add a place */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Add a place by name
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="e.g. Wayan, The Slipper Room"
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPlace()}
            size="small"
            sx={{ flex: '1 1 200px' }}
          />
          <Button
            variant="contained"
            onClick={handleAddPlace}
            disabled={isImporting || !placeName.trim()}
            startIcon={isImporting ? <CircularProgress size={18} color="inherit" /> : <AddLocationIcon />}
            sx={{ ...touchTargetSx, minWidth: 120 }}
          >
            Add
          </Button>
        </Box>
        {addPlaceResult && (
          <Alert
            severity={addPlaceResult.success ? 'success' : 'error'}
            sx={{ mt: 1 }}
            onClose={() => setAddPlaceResult(null)}
          >
            {addPlaceResult.message}
          </Alert>
        )}
      </Box>

      <Divider />

      {/* Add places around me */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Add places around you
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Max places:
            </Typography>
            {MAX_PLACES_OPTIONS.map((n) => (
              <Chip
                key={n}
                label={n}
                onClick={() => {
                  setMaxPlaces(n);
                  localStorage.setItem(STORAGE_KEY_LIMIT, String(n));
                }}
                color={maxPlaces === n ? 'primary' : 'default'}
                variant={maxPlaces === n ? 'filled' : 'outlined'}
                sx={{ minHeight: 36 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleAroundMe}
            disabled={isSearching}
            startIcon={isSearching ? <CircularProgress size={18} /> : <MyLocationIcon />}
            sx={touchTargetSx}
          >
            {isSearching ? 'Searching…' : 'Find places near me'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setPlaceTypesModalOpen(true)}
            startIcon={<TuneIcon />}
            sx={touchTargetSx}
          >
            Types ({selectedPlaceTypes.length})
          </Button>
          </Box>
        </Box>

        {aroundMePlaces.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                {aroundMePlaces.filter((p) => !p.isDuplicate).length} places • {selectedPlaceIds.size} selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={handleImportAroundMe}
                disabled={isImporting || selectedPlaceIds.size === 0}
              >
                Import selected
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
              {aroundMePlaces.map((p) => (
                <ListItem key={p.id} disablePadding>
                  <ListItemButton
                    onClick={() => togglePlaceSelection(p.id)}
                    disabled={p.isDuplicate}
                    sx={{ ...touchTargetSx, opacity: p.isDuplicate ? 0.6 : 1 }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedPlaceIds.has(p.id)}
                      disabled={p.isDuplicate}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={p.name}
                      secondary={p.isDuplicate ? 'Already in database' : p.type}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {aroundMeResult && (
          <Alert
            severity={aroundMeResult.success ? 'success' : 'error'}
            sx={{ mt: 1 }}
            onClose={() => setAroundMeResult(null)}
          >
            {aroundMeResult.message}
          </Alert>
        )}
      </Box>

      {/* Place types modal */}
      <Dialog
        open={placeTypesModalOpen}
        onClose={() => setPlaceTypesModalOpen(false)}
        fullScreen={false}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            m: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Place types
          <IconButton onClick={() => setPlaceTypesModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tap to add or remove. Empty = all types.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {OSM_PLACE_TYPES.map((t) => (
              <Chip
                key={t.value}
                label={t.label}
                onClick={() => togglePlaceType(t.value)}
                color={selectedPlaceTypes.includes(t.value) ? 'primary' : 'default'}
                variant={selectedPlaceTypes.includes(t.value) ? 'filled' : 'outlined'}
                sx={{ minHeight: 40 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaceTypesModalOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickPlaceImport;
